import { ColumnConfig } from '@blisspointmedia/bpm-types/dist/ColumnConfig';
import { App2Channels, TVAD_CHANNELS } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { Formatter } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { IconSpec } from '@blisspointmedia/bpm-types/dist/dimensionIcons';
import { formatDateLabel } from '@src/utils/date-utils';
import { getDimensionIcon } from '@src/utils/columnMetricSystem/adapters';
import {
  getConfig,
  getBenchmarkingDimensionCols,
  getBenchmarkingDataCols,
  colOrMetricRequiresKpi,
  DimensionColNames
} from '@blisspointmedia/bpm-types/dist/Columns';
import { isKnownMetric, MASTER_METRIC_MAP } from '@blisspointmedia/bpm-types/dist/Metrics';

export const getIabVerticalLabel = (iab: string): string => {
  if (iab === 'foo') {
    return '';
  }
  const label = IAB_CATEGORY_MAP[iab];
  return label ?? iab;
};

const IAB_CATEGORY_MAP = {
  IAB1: 'IAB1 Arts & Entertainment',
  IAB2: 'IAB2 Automotive',
  IAB3: 'IAB3 Business',
  IAB4: 'IAB4 Careers',
  IAB5: 'IAB5 Education',
  IAB6: 'IAB6 Family & Parenting',
  IAB7: 'IAB7 Health & Fitness',
  IAB8: 'IAB8 Food & Drink',
  IAB9: 'IAB9 Hobbies & Interests',
  IAB10: 'IAB10 Home & Garden',
  IAB11: "IAB11 Law, Gov't & Politics",
  IAB12: 'IAB12 News',
  IAB13: 'IAB13 Personal Finance',
  IAB14: 'IAB14 Society',
  IAB15: 'IAB15 Science',
  IAB16: 'IAB16 Pets',
  IAB17: 'IAB17 Sports',
  IAB18: 'IAB18 Style & Fashion',
  IAB19: 'IAB19 Technology & Computing',
  IAB20: 'IAB20 Travel',
  IAB21: 'IAB21 Real Estate',
  IAB22: 'IAB22 Shopping',
  IAB23: 'IAB23 Religion & Spirituality',
  IAB24: 'IAB24 Uncategorized'
};

type DimensionFormatter = (raw: string | number | null) => string;
export interface ColOverride {
  prettyName: string;
  formatter?: DimensionFormatter;
}
export interface ResolvedDimConfig extends Pick<ColumnConfig, 'name' | 'prettyName'> {
  iconStyle?: IconSpec['style'];
  formatter?: DimensionFormatter;
}

export interface ResolvedDataConfig {
  name: string;
  prettyName: string;
  formatter?: Formatter;
}

// Pretty-name + optional formatter overrides for cols not registered in bpm-types'
// column metric system (e.g. `date`, `company`). Used as a fallback when
// getBenchmarkingDimensionCols / getBenchmarkingDataCols / getConfig all return
// nothing for a name.
export const COL_OVERRIDES = {
  date: {
    prettyName: 'Date',
    formatter: raw => (raw == null ? '' : formatDateLabel(String(raw), {}))
  },
  week: {
    prettyName: 'Week',
    formatter: raw => (raw == null ? '' : formatDateLabel(String(raw), {}, 'Week'))
  },
  month: {
    prettyName: 'Month',
    formatter: raw => (raw == null ? '' : formatDateLabel(String(raw), {}, 'Month'))
  },
  company: { prettyName: 'Client' },
  tactic: { prettyName: 'Tactic' },
  beeswax_iab_category: {
    prettyName: 'Vertical',
    formatter: raw => (raw == null ? '' : getIabVerticalLabel(String(raw)))
  }
} satisfies Record<string, ColOverride>;

// Returns the metric column names for a given channel:
//   - all benchmarking-enabled data cols for the channel (from bpm-types), minus KPI-requiring
//     ones when the channel has no performance data (the lambda rejects those without a KPI)
//   - plus `spend` (always)
//   - plus `revenue` and `volume` if the channel is TVAD and has performance data
export const getMetricsForChannel = (channel: App2Channels, hasPerformance: boolean): string[] => {
  const metrics = new Set<string>();
  metrics.add('spend');
  for (const cfg of getBenchmarkingDataCols(channel)) {
    if (hasPerformance || !colOrMetricRequiresKpi(cfg.name)) {
      metrics.add(cfg.name);
    }
  }
  if (TVAD_CHANNELS.has(channel) && hasPerformance) {
    metrics.add('revenue');
    metrics.add('volume');
  }
  return [...metrics];
};

// Resolves a column name to { name, prettyName, formatter? } by checking, in order:
//   1. the channel's benchmarking dim configs
//   2. bpm-types' master column config (getConfig)
//   3. COL_OVERRIDES for cols bpm-types doesn't know about (e.g. `date`, `company`)
export const resolveDimConfig = (
  name: string,
  channel: App2Channels
): ResolvedDimConfig | undefined => {
  const benchDims = getBenchmarkingDimensionCols(channel);
  const fromBench = benchDims.find(c => c.name === name);
  if (fromBench)
    return {
      name: fromBench.name,
      prettyName: fromBench.prettyName,
      iconStyle: getDimensionIcon(fromBench.name)?.style
    };
  const cfg = getConfig(name as Parameters<typeof getConfig>[0]);
  if (cfg) {
    return {
      name: cfg.name,
      prettyName: cfg.prettyName,
      iconStyle: getDimensionIcon(cfg.name)?.style
    };
  }
  const override = COL_OVERRIDES[name];
  if (override) return { name, prettyName: override.prettyName, formatter: override.formatter };
  return undefined;
};

// Resolves a data column name to { name, prettyName, formatter }. Same fallback ladder as
// resolveDimConfig but checks getBenchmarkingDataCols + MASTER_METRIC_MAP for metrics. Every
// value (cells and totals) is computed server-side, so no client aggregation config is needed.
export const resolveDataConfig = (
  name: string,
  channel: App2Channels
): ResolvedDataConfig | undefined => {
  const benchData = getBenchmarkingDataCols(channel);
  const fromBench = benchData.find(c => c.name === name);
  if (fromBench) {
    return {
      name: fromBench.name,
      prettyName: fromBench.prettyName,
      formatter: fromBench.formatter
    };
  }
  if (isKnownMetric(name)) {
    const metric = MASTER_METRIC_MAP[name];
    return {
      name: metric.name,
      prettyName: metric.prettyName,
      formatter: metric.formatter
    };
  }
  const cfg = getConfig(name as Parameters<typeof getConfig>[0]);
  if (cfg) return { name: cfg.name, prettyName: cfg.prettyName, formatter: cfg.formatter };
  const override = COL_OVERRIDES[name];
  if (override) return { name, prettyName: override.prettyName };
  return undefined;
};

const PLATFORM_DIM = ['platform'] as const;

// Non-platform dimension presets per channel. `['platform']` is prepended for every
// channel in the reduce below, so it should not appear here.
// NOTE: `searchShopping` is camelCase to match the App2Channels union in bpm-types.
type PresetDimensionsList = readonly (readonly (
  DimensionColNames | keyof typeof COL_OVERRIDES
)[])[];
type PrestDimensionOptionsByChannel = Partial<Record<App2Channels, PresetDimensionsList>>;

const LINEAR_PRESETS: PresetDimensionsList = [
  ['channel', 'network_group'],
  ['channel', 'network_short_code'],
  ['channel', 'network_group', 'network_short_code'],
  ['month', 'channel', 'network_group'],
  ['month', 'channel', 'network_short_code'],
  ['month', 'channel', 'network_group', 'network_short_code'],
  ['month', 'channel', 'network_group', 'beeswax_iab_category'],
  ['month', 'channel', 'network_short_code', 'beeswax_iab_category'],
  ['month', 'channel', 'network_group', 'network_short_code', 'beeswax_iab_category'],
  ['month', 'company', 'channel', 'platform', 'network_group'],
  ['month', 'company', 'channel', 'platform', 'network_short_code'],
  ['month', 'company', 'channel', 'platform', 'network_group', 'network_short_code'],
  ['channel', 'platform', 'network_group'],
  ['channel', 'platform', 'network_short_code'],
  ['channel', 'platform', 'network_group', 'network_short_code']
];

export const PRESET_DIMENSION_OPTIONS_BY_CHANNEL: PrestDimensionOptionsByChannel = {
  social: [
    ['date', 'platform', 'company'], // too large a data set wip
    ['platform', 'beeswax_iab_category']
  ],
  commerce: [
    ['month', 'channel', 'platform', 'retailer', 'campaign_type'],
    ['month', 'channel', 'platform', 'retailer', 'campaign_type', 'beeswax_iab_category'],
    [
      'month',
      'channel',
      'platform',
      'retailer',
      'company',
      'campaign_type',
      'beeswax_iab_category'
    ],
    ['channel', 'platform'],
    ['channel', 'platform', 'retailer', 'campaign_type']
  ],
  searchShopping: [
    ['channel', 'platform', 'tactic'],
    ['channel', 'platform', 'beeswax_iab_category'],
    ['channel', 'platform', 'beeswax_iab_category', 'tactic']
    // ['date', 'platform', 'company']
  ],
  youtube: [],
  streaming: [
    ['network_name'],
    ['channel', 'network_name'],
    ['month', 'channel', 'network_name'],
    ['month', 'channel', 'network_name', 'beeswax_iab_category'],
    [
      'month',
      'channel',
      'platform',
      'company',
      'network_group',
      'network_name',
      'beeswax_iab_category'
    ],
    ['channel', 'platform', 'network_name']
  ],
  audio: [
    ['network_name'],
    ['channel', 'network_group', 'network_name'],
    ['month', 'channel', 'network_group', 'network_name'],
    ['month', 'channel', 'network_group', 'network_name', 'beeswax_iab_category'],
    ['month', 'channel', 'platform', 'company', 'network_group', 'network_name'],
    ['channel', 'platform', 'network_group', 'network_name']
  ],
  display: [
    ['network_name'],
    ['channel', 'network_group', 'network_name'],
    ['month', 'channel', 'network_group', 'network_name'],
    ['month', 'channel', 'network_group', 'network_name', 'beeswax_iab_category'],
    ['month', 'channel', 'platform', 'company', 'network_group', 'network_name'],
    ['channel', 'platform', 'network_group', 'network_name']
  ],
  linear: LINEAR_PRESETS,
  // Radio shares linear's benchmarking dimensions.
  radio: LINEAR_PRESETS
};

export interface PresetDropdownOption {
  label: string;
  value: string;
}

export const DROPDOWN_PRESET_OPTIONS_BY_CHANNEL = (
  Object.entries(PRESET_DIMENSION_OPTIONS_BY_CHANNEL) as [App2Channels, PresetDimensionsList][]
).reduce<Partial<Record<App2Channels, PresetDropdownOption[]>>>((acc, [channel, dimsList]) => {
  acc[channel] = [PLATFORM_DIM, ...dimsList].map(dims => ({
    label: dims.map(item => resolveDimConfig(item, channel)?.prettyName ?? item).join(', '),
    value: dims.join(',')
  }));
  return acc;
}, {});
